/**
 * useRealtime.js — Socket.io → TanStack Query cache invalidation
 *
 * Connects to the Express backend's WebSocket endpoint.
 * When a remote change arrives (INSERT, UPDATE, DELETE from another user),
 * the corresponding TanStack Query cache is invalidated, triggering a refetch.
 *
 * This gives instant multi-user reactivity without manual polling.
 *
 * Usage: Call useRealtime() once in App.jsx (inside QueryClientProvider).
 * It subscribes on mount and cleans up on unmount.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { tableToQueryKey } from '../lib/queryClient';

/**
 * Subscribe to WebSocket changes on all data tables.
 * Invalidates the matching TanStack Query cache on every change.
 */
export function useRealtime() {
  const queryClient = useQueryClient();
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io({
      path: '/ws',
      transports: ['websocket'],
    });

    socket.on('connect', () => {
      console.log('[Realtime] Connected via WebSocket');
    });

    socket.on('db-change', (payload) => {
      const { table, data } = payload;

      // Invalidate the query cache for this table
      const queryKey = tableToQueryKey[table];
      if (queryKey) {
        queryClient.invalidateQueries({ queryKey });
      }

      // For tables with detail queries, also invalidate the specific record
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: [table, data.id],
        });
      }

      // If it's a job-related table change, also invalidate the parent job
      if (data?.job_id) {
        queryClient.invalidateQueries({
          queryKey: ['jobs', data.job_id],
        });
      }
    });

    socket.on('disconnect', () => {
      console.warn('[Realtime] Disconnected — will auto-reconnect');
    });

    socketRef.current = socket;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [queryClient]);
}
